import { supabase } from './supabase';

/**
 * Secure API Service
 *
 * Uses Supabase's built-in session management for authenticated API calls.
 * No need to manually manage tokens - Supabase handles everything securely.
 */

class ApiService {
  /**
   * Make an authenticated API call using Supabase's session
   */
  async authenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    // Get the current session from Supabase
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('No valid session found');
    }

    // Add authorization header with the current access token
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...options.headers,
    };

    return fetch(url, {
      ...options,
      headers,
    });
  }

  /**
   * Make a Supabase RPC call (most secure for database operations)
   */
  async rpcCall<T = unknown>(
    functionName: string,
    params?: Record<string, unknown>
  ): Promise<{ data: T | null; error: unknown }> {
    const { data, error } = await supabase.rpc(functionName, params);
    return { data, error };
  }

  /**
   * Make a Supabase query (automatically uses current session)
   */
  async query<T = Record<string, unknown>>(
    table: string,
    query: {
      select?: string;
      filters?: Record<string, unknown>;
      likeFilters?: Record<string, unknown>; // New: for partial matching
      orderBy?: { column: string; ascending?: boolean };
      limit?: number;
      offset?: number;
    }
  ): Promise<{ data: T[] | null; error: unknown }> {
    let supabaseQuery = supabase.from(table).select(query.select || '*');

    // Apply exact match filters
    if (query.filters) {
      Object.entries(query.filters).forEach(([key, value]) => {
        supabaseQuery = supabaseQuery.eq(key, value);
      });
    }

    // Apply partial match filters using ilike (case-insensitive)
    if (query.likeFilters) {
      Object.entries(query.likeFilters).forEach(([key, value]) => {
        if (value && typeof value === 'string' && value.trim()) {
          supabaseQuery = supabaseQuery.ilike(key, `%${value.trim()}%`);
        }
      });
    }

    // Apply ordering
    if (query.orderBy) {
      supabaseQuery = supabaseQuery.order(query.orderBy.column, {
        ascending: query.orderBy.ascending ?? true,
      });
    }

    // Apply pagination
    if (query.limit) {
      supabaseQuery = supabaseQuery.limit(query.limit);
    }
    if (query.offset) {
      supabaseQuery = supabaseQuery.range(
        query.offset,
        query.offset + (query.limit || 10) - 1
      );
    }

    const { data, error } = await supabaseQuery;
    return { data: data as T[] | null, error };
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return !!session?.user;
  }

  /**
   * Get current user ID
   */
  async getCurrentUserId(): Promise<string | null> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.user?.id || null;
  }

  /**
   * Get current access token (for external API calls if needed)
   */
  async getAccessToken(): Promise<string | null> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || null;
  }
}

// Export singleton instance
export const apiService = new ApiService();
